import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import dayjs from 'dayjs';

type Member = Awaited<
  ReturnType<typeof rpx.coursesOverview.getCourseMembersForOverview>
>['members'][number];

type SourceFilter = 'all' | 'standalone' | 'cohort_bundle' | 'invited' | 'orphan';

const SOURCE_LABELS: Record<SourceFilter, string> = {
  all: 'All',
  standalone: 'Standalone',
  cohort_bundle: 'From bundle',
  invited: 'Invited',
  orphan: 'Orphan',
};

export function CourseMembersDrawer(props: { courseId: UUID; title: string; onClose: () => void }) {
  const [members, setMembers] = useState<Member[] | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SourceFilter>('all');

  async function refresh() {
    try {
      const result = await rpx.coursesOverview.getCourseMembersForOverview({
        courseId: props.courseId,
      });
      setMembers(result.members);
    } catch (err) {
      showError(err);
    }
  }

  useEffect(() => {
    refresh();
  }, [props.courseId]);

  const filtered = useMemo(() => {
    if (!members) {
      return [];
    }
    let rows = members;
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (m) => (m.name || '').toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      );
    }
    if (filter !== 'all') {
      rows = rows.filter((m) => m.source === filter);
    }
    return rows;
  }, [members, query, filter]);

  return createPortal(
    <div class="fixed inset-0 z-[1000] bg-black/30" onClick={props.onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        onClick={(e) => e.stopPropagation()}
        class="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col border-l border-gray-200 bg-white shadow-xl"
      >
        <header class="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 class="text-base font-semibold text-gray-900">
              {props.title}{' '}
              <span class="text-sm font-normal text-gray-500">
                ({filtered.length}
                {members && filtered.length !== members.length ? ` of ${members.length}` : ''})
              </span>
            </h2>
            <p class="text-xs text-gray-500">Enrolled members and how they got here.</p>
          </div>
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-700"
            onClick={props.onClose}
          >
            Close
          </button>
        </header>
        <div class="border-b border-gray-200 px-5 py-3">
          <input
            type="text"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            placeholder="Search by name or email…"
            class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <div class="mt-2 flex flex-wrap gap-1.5">
            {(Object.keys(SOURCE_LABELS) as SourceFilter[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                class={`rounded-full border px-2.5 py-1 text-xs ${
                  filter === k
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {SOURCE_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        <div class="flex-1 overflow-y-auto">
          {!members ? (
            <div class="p-6">
              <LoadingIndicator />
            </div>
          ) : filtered.length === 0 ? (
            <p class="p-6 text-sm text-gray-500">
              {members.length === 0
                ? 'No members enrolled yet.'
                : 'No members match these filters.'}
            </p>
          ) : (
            <ul class="divide-y divide-gray-100">
              {filtered.map((m) => (
                <li key={m.userId} class="flex items-baseline justify-between px-5 py-3">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-gray-900">{m.name || m.email}</p>
                    <p class="truncate text-xs text-gray-500">{m.email}</p>
                  </div>
                  <div class="ml-3 flex shrink-0 items-baseline gap-2">
                    <SourceBadge source={m.source} />
                    <span class="text-xs text-gray-400">
                      {dayjs(m.membershipCreatedAt).format('MMM D')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SourceBadge(props: { source: Member['source'] }) {
  const labels: Record<Member['source'], { label: string; cls: string }> = {
    standalone: { label: 'Standalone', cls: 'bg-emerald-50 text-emerald-700' },
    cohort_bundle: { label: 'From bundle', cls: 'bg-indigo-50 text-indigo-700' },
    invited: { label: 'Invited', cls: 'bg-gray-100 text-gray-600' },
    orphan: { label: 'Orphan', cls: 'bg-amber-50 text-amber-700' },
  };
  const meta = labels[props.source];
  return (
    <span class={`rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>{meta.label}</span>
  );
}
